/* eslint-disable prettier/prettier */
import { computed, createEnvironmentInjector, effect, EnvironmentInjector, EnvironmentProviders, inject, Injectable, Injector, Provider, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControlConfigType } from '@app/core/enums/form-control-config-type';
import {
  IFloatingPopupConfig,
  IFloatingPopupGroupInstance,
  IFloatingPopupInstance,
} from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { IEnumFormControlConfig } from '@app/core/interfaces/form-control-config';
import { map, of } from 'rxjs';
import { ScreenResolutionService } from '@app/core/services/screen-resolution/screen-resolution.service';
import {
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
  MIN_HEIGHT,
  VIEWPORT_VERTICAL_MARGIN,
} from '@app/main/main-content/floating-popup-manager/configs/floating-popup.config';
import { FloatingPopupAnimationCallbackRegistry } from '@app/main/main-content/floating-popup-manager/services/floating-popup-animation-callback-registry.service';
import { FloatingPopupTabsService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-tabs.service';
import { FloatingPopupComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup/floating-popup.component';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { IFloatingPopupHibernationData } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-hibernation';
import { FloatingPopupHibernationService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-hibernation.service';
import { CssHelperService } from '@app/core/services/css-helper/css-helper.service';
import { _, TranslateService } from '@ngx-translate/core';
import { WINDOW } from '@app/core/providers/window/window.token';
import { ITranslationSubscription } from '@app/core/interfaces/localization';

// todo: add subscription to popup and area change and close popups if they are changed
@Injectable({ providedIn: 'root' })
export class FloatingPopupService {
  private readonly injector = inject(Injector);
  private readonly window = inject(WINDOW);
  private readonly cssHelper = inject(CssHelperService);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly translateService = inject(TranslateService);
  private readonly popupAnimationCallbackRegistry = inject(FloatingPopupAnimationCallbackRegistry);
  private readonly floatingPopupTabs = inject(FloatingPopupTabsService);
  private readonly floatingPopupHibernation = inject(FloatingPopupHibernationService);
  private scrRes: ScreenResolutionService = inject(ScreenResolutionService);

  private startZIndex = +(this.cssHelper.readCssVariable('--z-screen-overlay') || 1900);

  public isMobileMode = signal<boolean>(false);
  public isTrayMinimized = signal<boolean>(false);

  private floatingPopupGroups = new Map<string, IFloatingPopupGroupInstance>();
  public popupActiveGroupId = signal<string | null>(null);

  public popups = signal<IFloatingPopupInstance[]>([]);
  public minimizedPopups = computed(() => this.popups().filter(p => p.isMinimized));
  public minimizedPopupsForActiveGroup = computed(() =>
    this.minimizedPopups()
      .filter(p => p.groupId === this.popupActiveGroupId())
      .sort((a, b) => b.lastPopupFocusedAt - a.lastPopupFocusedAt)
  );
  public activeGroupPopupsForMobile = computed(() =>
    this.popups()
      .filter(p => p.groupId === this.popupActiveGroupId())
      .sort((a, b) => this.sortingForMobilePopups(a, b))
  );

  private defaultGroup: IFloatingPopupGroupInstance = {
    id: 'default-group',
    orderWeight: Number.MAX_SAFE_INTEGER,
    minimizeTitle: (count: number) => this.translateService.instant(_('@@floating-popup-group-default-name'), { I18N_VAR_COUNT: count }),
  };

  constructor() {
    effect(() => {
      const isScreenLgUp = this.scrRes.isScreenLgUp();

      if (!isScreenLgUp) {
        this.activateMobileMode();
      } else {
        this.activateDesktopMode();
      }
    });
  }

  public open<T>(floatingPopupConfig: IFloatingPopupConfig, data: T, groupConfig?: IFloatingPopupGroupInstance) {
    const currentPopups = this.popups();

    const group = groupConfig || this.defaultGroup;

    if (group.isSingleUnit) {
      const existing = currentPopups.find(p => p.groupId === group.id);
      if (existing) {
        this.focusSingleUnitPopup(existing.id, floatingPopupConfig.minimizedPreferred);
        return;
      }
    }

    const offset = currentPopups.length * 30;

    const injector = this.createInjector(data, floatingPopupConfig.instanceProviders || []);

    const popupHeight = floatingPopupConfig.height ?? DEFAULT_HEIGHT;

    const {
      width: popupWidth,
      height: popupHeightClamped,
      x: centerX,
      y: centerY,
    } = this.calculatePopupPosition(floatingPopupConfig, popupHeight, offset);

    const minimizedPreferred = floatingPopupConfig.minimizedPreferred ?? false;

    const defaultMinimizedState = this.isMobileMode() && group.isSingleUnit ? false : minimizedPreferred;

    const newPopup: IFloatingPopupInstance = {
      groupId: group.id,
      isMinimized: defaultMinimizedState,
      minimizedPreferred,
      isFullscreen: false,

      isPopupAnimationInProgress: false,

      popupComponent: FloatingPopupComponent,

      ...floatingPopupConfig,
      width: popupWidth,
      height: popupHeightClamped,
      injector,
      id: crypto.randomUUID(),
      x: centerX,
      y: centerY,
      zIndex: this.startZIndex + currentPopups.length,
      createdAt: Date.now(),
      lastPopupFocusedAt: Date.now(),

      title: floatingPopupConfig.title || of('Untitled Popup'),
      shortTitle: floatingPopupConfig.shortTitle || of('Untitled Popup'),
    };

    this.initFloatingPopupGroup(group.id, group);

    this.floatingPopupTabs.setActive(
      newPopup.id,
      floatingPopupConfig.selectedTab || groupConfig?.tabs?.values?.[0] || null
    );

    this.popups.set([...currentPopups, newPopup]);

    if (newPopup.isMinimized) {
      this.setPopupActiveGroupId(group.id);
    }

    newPopup.actions?.onOpen?.(newPopup, this.isMobileMode());
  }

  public close(id: string, isHibernation = false) {
    const popupToClose = this.popups().find(p => p.id === id);

    if (!popupToClose) {
      console.error(`Popup with id ${id} does not exist`);
      return;
    }

    this.popups.set(this.popups().filter(p => p.id !== id));

    if (isHibernation) {
      popupToClose.actions?.onHibernation?.(popupToClose, this.isMobileMode());
    } else {
      popupToClose.actions?.onClose?.(popupToClose, this.isMobileMode());
    }

    popupToClose.injector?.destroy();

    popupToClose.actions?.onDestroy?.(popupToClose, this.isMobileMode());

    this.floatingPopupTabs.deleteFromActive(id);
    this.popupAnimationCallbackRegistry.delete(id);

    this.checkPopupActiveGroup();
  }

  public closeAll(isHibernation = false) {
    this.popups().forEach(p => this.close(p.id, isHibernation));
  }

  public closeGroup(groupId: string) {
    this.popups()
      .filter(p => p.groupId === groupId)
      .forEach(p => this.close(p.id));
  }

  public maximizeAllPopupsInGroup(groupId: string) {
    this.popups()
      .filter(p => p.groupId === groupId)
      .forEach(p => this.maximize(p.id, true));
  }

  public restoreAllFromHibernation(hibernationKey: string) {
    const hibernationData = this.floatingPopupHibernation.getHibernationData(hibernationKey);

    hibernationData.forEach(data => this.restoreOneFromHibernation(data));

    this.floatingPopupHibernation.clearHibernationData(hibernationKey);

    this.updateZIndex();

    if (!this.isMobileMode()) {
      this.floatingPopupTabs.normalizeMinimizedPopupActiveTabs(this.minimizedPopups());
    }
  }

  public hibernateAllExisting(hibernationKey: string) {
    if (!this.popups().length) {
      return;
    }

    const popupForHibernation: IFloatingPopupHibernationData<object | unknown>[] = this.popups().map(floatingPopupInstance => {
      return {
        floatingPopupInstance,
        data: floatingPopupInstance.injector.get<object | unknown>(FLOATING_POPUP_DATA),
        groupConfig: this.floatingPopupGroups.get(floatingPopupInstance.groupId),
        selectedTab: this.floatingPopupTabs.getActive(floatingPopupInstance.id)(),
      };
    });

    this.floatingPopupHibernation.setHibernationData(hibernationKey, popupForHibernation);

    this.closeAll(true);
  }

  public clearAllHibernationData() {
    this.floatingPopupHibernation.clearAllHibernationData();
  }

  public finishPopupAnimation(id: string) {
    this.updateState(id, { isPopupAnimationInProgress: false });

    this.popupAnimationCallbackRegistry.execute(id);
  }

  public minimize(id: string, useTrayPopupActiveTab = true, provideAnimation = true) {
    const currentPopup = this.popups().find(p => p.id === id);

    if (!currentPopup) {
      console.log(`Popup with id ${id} does not exist`);
      return;
    }

    this.maximizeTray();

    const minimizedPopupActiveTab = this.floatingPopupTabs.getMinimizedPopupActiveTabForGroup(
      currentPopup.groupId,
      this.minimizedPopups()
    );

    this.updateState(id, {
      isMinimized: true,
      isPopupAnimationInProgress: provideAnimation,
      lastPopupFocusedAt: Date.now(),
    });

    if (useTrayPopupActiveTab && minimizedPopupActiveTab) {
      if (provideAnimation) {
        this.popupAnimationCallbackRegistry.add(id, () => {
          this.floatingPopupTabs.setActive(id, minimizedPopupActiveTab);
        });
      } else {
        this.floatingPopupTabs.setActive(id, minimizedPopupActiveTab);
      }
    }

    this.setPopupActiveGroupId(currentPopup.groupId);

    currentPopup.actions?.onMinimize?.(currentPopup, this.isMobileMode());
  }

  public maximize(id: string, provideAnimation = true) {
    const popup = this.popups().find(p => p.id === id);

    const widthPatch = popup?.minWidth && popup.width < popup.minWidth
      ? { width: popup.minWidth }
      : {};

    const heightPatch = popup ? this.clampHeightToViewport(popup.height, popup.y) : {};

    this.updateState(id, {
      isMinimized: false,
      isPopupAnimationInProgress: provideAnimation,
      ...widthPatch,
      ...heightPatch,
    });

    this.focus(id);

    this.checkPopupActiveGroup();

    const currentPopup = this.popups().find(p => p.id === id);

    if (!currentPopup) {
      console.log(`Popup with id ${id} does not exist`);
      return;
    }

    currentPopup.actions?.onMaximize?.(currentPopup, this.isMobileMode());
  }

  public minimizeTray() {
    this.isTrayMinimized.set(true);
  }

  public maximizeTray() {
    this.isTrayMinimized.set(false);
  }

  public minimizeAllPopup(useTrayPopupActiveTab: boolean, animation: boolean, ignorePopupId?: string) {
    this.popups().forEach(p => {
      if (p.isMinimized || ignorePopupId === p.id) {
        return;
      }

      this.minimize(p.id, useTrayPopupActiveTab, animation);
    });
  }

  public closeAllMinimized() {
    const minimizedPopups = this.minimizedPopups();

    minimizedPopups.forEach(popup => this.close(popup.id));
  }

  public focus(id: string) {
    this.updateState(id, { lastPopupFocusedAt: Date.now() });

    this.updateZIndex();
  }

  public enterFullscreen(id: string, provideAnimation = true) {
    const currentPopup = this.popups().find(p => p.id === id);

    if (!currentPopup || currentPopup.isFullscreen) return;

    this.updateState(id, { isFullscreen: true, isPopupAnimationInProgress: provideAnimation });

    this.focus(id);
  }

  public exitFullscreen(id: string, provideAnimation = true) {
    const currentPopup = this.popups().find(p => p.id === id);

    if (!currentPopup || !currentPopup.isFullscreen) return;

    this.updateState(id, { isFullscreen: false, isPopupAnimationInProgress: provideAnimation });

    this.focus(id);
  }

  public updateState(id: string, patch: Partial<IFloatingPopupInstance>) {
    const updated = this.popups().map(p => (p.id === id ? { ...p, ...patch } : p));
    this.popups.set(updated);
  }

  public updateTitleInfo(id: string, titleInfo: ITranslationSubscription | undefined) {
    this.updateState(id, { titleInfo });
  }

  public getAllGroups(): IFloatingPopupGroupInstance[] {
    return Array.from(this.floatingPopupGroups.values()).sort((a, b) => a.orderWeight - b.orderWeight);
  }

  public setPopupActiveGroupId(groupId: string | null) {
    this.popupActiveGroupId.set(groupId);
  }

  public sortingForMobilePopups(a: IFloatingPopupInstance, b: IFloatingPopupInstance): number {
    return a.createdAt - b.createdAt;
  }

  public isPopupActive(popup: IFloatingPopupInstance): boolean {
    return !(popup.isMinimized && !popup.isPopupAnimationInProgress);
  }

  public prepareFormControlConfigForPopupTab(popupId: string): IEnumFormControlConfig<string> | null {
    const popup = this.popups().find(p => p.id === popupId);
    if (!popup) {
      console.error(`Popup with id ${popupId} does not exist`);
      return null;
    }

    const groupId = popup.groupId;
    if (!groupId) {
      console.error(`Popup with id ${popupId} does not have a groupId`);
      return null;
    }

    const group = this.floatingPopupGroups.get(groupId);
    if (!group) {
      console.error(`Group with id ${groupId} does not exist`);
      return null;
    }

    return this.floatingPopupTabs.getFormControlConfig(popup, group.tabs);
  }

  public prepareEnumFormControlConfigForGroupTab(groupId: string): IEnumFormControlConfig<string> | null {
    const group = this.floatingPopupGroups.get(groupId);
    if (!group) {
      console.error(`Group with id ${groupId} does not exist`);
      return null;
    }

    const tabs = group.tabs;
    if (!tabs) {
      return null;
    }

    return {
      ...tabs,
      id: `floating-popup-group-tabs-${groupId}`,
      type: FormControlConfigType.Enum,
      defaultValue: tabs.values[0],
      getValue: () =>
        toObservable(this.minimizedPopups, { injector: this.injector }).pipe(
          map(minimizedPopups => this.floatingPopupTabs.getMinimizedPopupActiveTabForGroup(groupId, minimizedPopups))
        ),
      valueChange: (value: string) => this.floatingPopupTabs.setGroupTab(groupId, value, this.minimizedPopups()),
    };
  }

  private focusSingleUnitPopup(id: string, minimizedPreferred?: boolean) {
    const popup = this.popups().find(p => p.id === id);
    if (!popup) return;

    if (popup.isMinimized) {
      if (minimizedPreferred) {
        this.setPopupActiveGroupId(popup.groupId);
      } else {
        this.maximize(id);
      }
    } else {
      this.focus(id);
    }
  }

  private createInjector<T>(data: T, instanceProviders: (Provider | EnvironmentProviders)[]): EnvironmentInjector {
    return createEnvironmentInjector(
      [{ provide: FLOATING_POPUP_DATA, useValue: data }, ...instanceProviders],
      this.environmentInjector
    );
  }

  private restoreOneFromHibernation<T = object | unknown>(hibernationData: IFloatingPopupHibernationData<T>) {
    const currentPopups = this.popups();

    const group = hibernationData.groupConfig || this.defaultGroup;

    const injector = this.createInjector(
      hibernationData.data,
      hibernationData.floatingPopupInstance.instanceProviders || []
    );

    const restoredPopup: IFloatingPopupInstance = {
      ...hibernationData.floatingPopupInstance,
      injector,
    };

    this.initFloatingPopupGroup(group.id, group);

    this.floatingPopupTabs.setActive(restoredPopup.id, hibernationData.selectedTab || hibernationData.groupConfig?.tabs?.values?.[0] || null);

    this.popups.set([...currentPopups, restoredPopup]);

    restoredPopup.actions?.onRestore?.(restoredPopup, this.isMobileMode());

    this.setPopupActiveGroupId(this.minimizedPopups()[0]?.groupId || null);
  }

  private getFocusedPopup(): IFloatingPopupInstance | null {
    const allPopups = this.popups();

    if (!allPopups.length) {
      return null;
    }

    const focusedPopup: IFloatingPopupInstance | null = allPopups.reduce(
      (popupWithNewestDate, popup) => {
        if (
          !popupWithNewestDate ||
          (!popup.isMinimized && popup.lastPopupFocusedAt > popupWithNewestDate.lastPopupFocusedAt)
        ) {
          return popup;
        }

        return popupWithNewestDate;
      },
      { ...allPopups[0] }
    );

    return focusedPopup.isMinimized ? null : focusedPopup;
  }

  private updateZIndex() {
    const currentPopups = this.popups();

    const zIndexLookup = new Map(
      [...currentPopups]
        .sort((a, b) => a.lastPopupFocusedAt - b.lastPopupFocusedAt)
        .map((p, index) => [p.id, this.startZIndex + index])
    );

    const updatedPopups = currentPopups.map(p => ({
      ...p,
      zIndex: zIndexLookup.get(p.id) || p.zIndex,
    }));

    this.popups.set(updatedPopups);
  }

  private activateMobileMode() {
    if (this.isMobileMode()) {
      return;
    }

    this.isMobileMode.set(true);

    const focusedPopup: IFloatingPopupInstance | null = this.getFocusedPopup();

    this.minimizeAllPopup(false, false, focusedPopup?.id);

    if (focusedPopup) {
      focusedPopup.actions?.onSwitchToMobile?.(focusedPopup, this.isMobileMode());
    }
  }

  private initFloatingPopupGroup(groupId: string, group: IFloatingPopupGroupInstance) {
    if (!this.floatingPopupGroups.has(groupId)) {
      this.floatingPopupGroups.set(groupId, group);
    }
  }

  private checkPopupActiveGroup() {
    const activeGroupId = this.popupActiveGroupId();
    if (activeGroupId) {
      const hasMinimizedPopupsInActiveGroup = this.minimizedPopups().some(p => p.groupId === activeGroupId);
      if (!hasMinimizedPopupsInActiveGroup) {
        this.setPopupActiveGroupId(this.minimizedPopups()[0]?.groupId || null);
      }
    }
  }

  private clampHeightToViewport(height: number, y: number): Partial<Pick<IFloatingPopupInstance, 'height' | 'y'>> {
    const winH = this.window.innerHeight;
    const maxHeight = Math.max(MIN_HEIGHT, winH - VIEWPORT_VERTICAL_MARGIN);

    if (height <= maxHeight) {
      return {};
    }

    return { height: maxHeight, y: Math.max(0, Math.min(y, winH - maxHeight - 10)) };
  }

  private calculatePopupPosition(
    config: IFloatingPopupConfig,
    popupHeight: number,
    offset: number
  ): { width: number; height: number; x: number; y: number } {
    const isLargeScreen = this.scrRes.isScreen2xlUp();
    const useAutoScale = isLargeScreen && !config.fixedWidth;

    const sidebarWidth = this.cssHelper.cssVarToPx('--sidebar-collapsed-width', 64);
    const mapRatio = parseFloat(this.cssHelper.readCssVariable('--floating-popup-map-ratio')) || 0.68;
    const leftMargin = this.cssHelper.cssVarToPx('--floating-popup-left-margin', 100);
    const rightGap = this.cssHelper.cssVarToPx('--floating-popup-right-gap', 24);

    const contentAreaWidth = this.window.innerWidth - sidebarWidth;
    const mapAreaWidth = contentAreaWidth * mapRatio;

    const width = useAutoScale
      ? Math.max(DEFAULT_WIDTH, mapAreaWidth - leftMargin - rightGap)
      : (config.width ?? DEFAULT_WIDTH);

    const x = useAutoScale
      ? Math.max(0, leftMargin + offset)
      : Math.max(0, (this.window.innerWidth - width) / 2 + offset);

    const height = Math.min(popupHeight, Math.max(MIN_HEIGHT, this.window.innerHeight - VIEWPORT_VERTICAL_MARGIN));

    const y = Math.max(0, (this.window.innerHeight - height) / 2 + offset);

    return { width, height, x, y };
  }

  private activateDesktopMode() {
    if (!this.isMobileMode()) {
      return;
    }

    this.isMobileMode.set(false);

    this.popups()
      .filter(p => !p.isMinimized && p.minimizedPreferred)
      .forEach(p => this.minimize(p.id, false, false));

    this.floatingPopupTabs.normalizeMinimizedPopupActiveTabs(this.minimizedPopups());
  }
}
